import { DatePipe } from '@angular/common';
import { Component, Input, ViewChild } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslatePipe } from '@ngx-translate/core';
import {
  BucketService,
  ICreatorKbEntry,
} from '../../../services/bucket.service';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import {
  AlTableApi,
  AlTableColumn,
} from '../../features/table/table.component';

/**
 * Creator-Inhalte in einer Wissensbasis (Reiter „Creator“): was der Creator
 * hineingestellt hat, mit live Version und ob sie hinter dem Inhalt liegt
 * (features/docs/technical/creator-items.md). Agent an/aus über die
 * datatypes-Zeile wie bei Dateien; Herausnehmen über die Creator-Route.
 */
@Component({
  selector: 'app-creator-items-table',
  templateUrl: './creator-items-table.component.html',
  styleUrl: './creator-items-table.component.scss',
})
export class CreatorItemsTableComponent {
  @Input() title = '';
  @Input() bucketId = '';
  @Input() loading = false;
  @Input() searchText = '';

  @ViewChild('table') table!: AlTableApi;

  // Regel 18: dieselbe Instanz zurückgeben, solange Quelle und Suche gleich sind
  private filterCache: { source: ICreatorKbEntry[]; query: string; result: ICreatorKbEntry[] } | null = null;

  get filteredData(): ICreatorKbEntry[] {
    const source = this.bucketService._creatorItems.value;
    const query = (this.searchText || '').toLowerCase();
    if (this.filterCache && this.filterCache.source === source && this.filterCache.query === query) {
      return this.filterCache.result;
    }
    const result = query ? source.filter((e) => e.name?.toLowerCase().includes(query)) : source;
    this.filterCache = { source, query, result };
    return result;
  }

  columns: AlTableColumn[] = [
    { key: 'name', title: this.t.transform('tables.creatorItems.name'), width: '40%', kind: 'name' },
    {
      key: 'creator_kind',
      title: this.t.transform('tables.creatorItems.kind'),
      map: (k: string) => (k ? this.t.transform('tables.creatorItems.kinds.' + k) : ''),
    },
    {
      key: 'version_no',
      title: this.t.transform('tables.creatorItems.version'),
      kind: 'number',
      map: (v: number, row: ICreatorKbEntry) =>
        v ? `v${v}${row?.outdated ? ' (!)' : ''}` : '',
    },
    {
      key: 'status',
      title: this.t.transform('tables.creatorItems.status'),
      map: (s: string) => (s ? this.t.transform('tables.creatorItems.states.' + s) : ''),
    },
    {
      key: 'agent_enabled',
      title: this.t.transform('tables.creatorItems.agent'),
      map: (v: any) =>
        this.t.transform(v === false ? 'tables.creatorItems.agentOff' : 'tables.creatorItems.agentOn'),
      width: '110px',
    },
    {
      key: 'published_at',
      kind: 'date',
      title: this.t.transform('tables.creatorItems.publishedAt'),
      map: (i: string) => (i ? new DatePipe('de-DE', 'CET').transform(i, 'short') : ''),
    },
  ];

  rowActions: any[] = [
    {
      key: 'toggleAgent',
      title: this.t.transform('tables.creatorItems.toggleAgent'),
      icon: 'slash-outline',
    },
    {
      key: 'withdraw',
      title: this.t.transform('tables.creatorItems.withdraw'),
      icon: 'minus-circle-outline',
    },
  ];

  constructor(
    protected bucketService: BucketService,
    protected dialogService: NbDialogService,
    protected nbToastrService: NbToastrService,
    protected t: TranslatePipe
  ) {}

  onEvent(e: { event: string; data: any }) {
    if (e.event !== 'rowAction') return;
    const { action, row } = e.data;
    if (action.key === 'toggleAgent') this.toggleAgentAccess(row);
    if (action.key === 'withdraw') this.withdraw(row);
  }

  toggleAgentAccess(row: ICreatorKbEntry) {
    if (!row?.datatype_id) return;
    const next = row.agent_enabled === false;
    this.bucketService.setFileAgentAccess(row.datatype_id, { agent_enabled: next }).subscribe({
      next: () => {
        row.agent_enabled = next;
        this.nbToastrService.success(
          this.t.transform(next ? 'tables.creatorItems.agentOnToast' : 'tables.creatorItems.agentOffToast'),
          this.t.transform('pages.knowledgeBase.title')
        );
      },
      error: () =>
        this.nbToastrService.danger(
          this.t.transform('tables.creatorItems.failed'),
          this.t.transform('pages.knowledgeBase.title')
        ),
    });
  }

  withdraw(row: ICreatorKbEntry) {
    if (!row?.creator_item_id || !this.bucketId) return;
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: 'tables.creatorItems.withdrawConfirmTitle',
          message: 'tables.creatorItems.withdrawConfirmMessage',
        },
      })
      .onClose.subscribe((confirmed) => {
        if (!confirmed) return;
        this.bucketService.withdrawCreatorItem(row.creator_item_id as string, this.bucketId).subscribe({
          next: () => {
            this.nbToastrService.success(
              this.t.transform('tables.creatorItems.withdrawStarted'),
              this.t.transform('pages.knowledgeBase.title')
            );
            // the job removes the entry in a few seconds; then the list follows
            setTimeout(() => this.bucketService.getBucketEntries(this.bucketId).subscribe(), 4000);
          },
          error: (err) =>
            this.nbToastrService.danger(
              err?.error?.message || this.t.transform('tables.creatorItems.failed'),
              this.t.transform('pages.knowledgeBase.title')
            ),
        });
      });
  }
}
